import React from 'react';
import { Box, Typography } from '@mui/material';
import { ChartSection, ScoreCard, ScoreLine, SectionDivide } from './panelSections';
import {
  capabilityScore,
  roleBreakdown,
  scoreCounts,
  strongestCapability,
  weakestCapability,
  type CapabilityPair,
  type Ratio
} from './teamCapability';
import CapabilityTrend from './CapabilityTrend';
import { trendFromApi } from './capabilityTrendData';
import { useCapabilityPairs, useCapabilityTrend } from '../../../../../../queries/useCapability';

const BRANDY = '#F79420';
const FONT = 'Inter, sans-serif';
export const TIP_TEXT = { fontFamily: FONT, fontSize: 11.5, lineHeight: 1.5, color: '#B6B6B6' };
export const TIP_HEADING = { ...TIP_TEXT, color: '#FDFDFD', fontWeight: 700 };
const RULE = '1px solid #3A3A3A';

// Past this many role lines, the sum is written as a total rather than spelled out.
const MAX_SPELLED_TERMS = 4;

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

interface Team {
  id: string;
  name: string;
}

interface CapabilityResult {
  capabilityId: string;
  name: string;
  ratio: Ratio;
}

/** One line of the Breakdown: what is counted on the left, the sum on the right. */
export const MathRow: React.FC<{
  label: React.ReactNode;
  value: React.ReactNode;
  ruleAbove?: boolean;
  total?: boolean;
}> = ({ label, value, ruleAbove = false, total = false }) => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'baseline',
      gap: '16px',
      py: '2px',
      ...(ruleAbove ? { borderTop: RULE, mt: '4px', pt: '6px' } : {}),
      // The result gets a double rule beneath it, so it reads as the answer.
      ...(total ? { borderTop: RULE, borderBottom: `3px double ${BRANDY}`, mt: '4px', pt: '6px', pb: '6px' } : {})
    }}
  >
    <Typography sx={{ ...TIP_TEXT, ...(total ? { color: '#FDFDFD', fontWeight: 700 } : {}) }}>{label}</Typography>
    <Typography
      sx={{
        ...TIP_TEXT,
        fontVariantNumeric: 'tabular-nums',
        whiteSpace: 'nowrap',
        textAlign: 'right',
        color: total ? BRANDY : '#FDFDFD',
        fontWeight: total ? 700 : 600
      }}
    >
      {value}
    </Typography>
  </Box>
);

/** The operator in a sum, dimmed so the numbers carry the line. */
export const Op: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box component="span" sx={{ color: '#8A8A8A', fontWeight: 400, mx: '4px' }}>
    {children}
  </Box>
);

/**
 * The Breakdown sum for a set of scores: one multiplication per team and role,
 * then the totals down to the result. Used for the Team Capability score and,
 * filtered to one capability, for the strongest and weakest.
 */
const Breakdown: React.FC<{ pairs: CapabilityPair[]; teams: Team[]; resultLabel: string; note?: string }> = ({
  pairs,
  teams,
  resultLabel,
  note
}) => {
  const teamName = (id: string) => teams.find((t) => t.id === id)?.name ?? 'Team';
  const score = capabilityScore(pairs);
  const counts = scoreCounts(pairs);
  const roles = roleBreakdown(pairs);
  const spellSum = roles.length > 1 && roles.length <= MAX_SPELLED_TERMS;
  return (
    <Box>
      <Typography sx={TIP_HEADING}>Breakdown</Typography>
      {note && (
        <Typography sx={{ ...TIP_TEXT, fontSize: 10.5, color: '#8A8A8A', mt: '4px', mb: '2px' }}>{note}</Typography>
      )}
      {roles.map((r) => (
        <MathRow
          key={`${r.teamId}|${r.roleId}`}
          label={`${teamName(r.teamId)} · ${r.roleName}`}
          value={
            <>
              {plural(r.employees, 'Employee', 'Employees')}
              <Op>×</Op>
              {plural(r.capabilities, 'Capability', 'Capabilities')}
              <Op>=</Op>
              {r.scores}
            </>
          }
        />
      ))}
      <MathRow
        ruleAbove
        label="Total Capabilities"
        value={
          spellSum ? (
            <>
              {roles.map((r, i) => (
                <React.Fragment key={`${r.teamId}|${r.roleId}`}>
                  {i > 0 && <Op>+</Op>}
                  {r.scores}
                </React.Fragment>
              ))}
              <Op>=</Op>
              {counts.scores}
            </>
          ) : (
            counts.scores
          )
        }
      />
      {counts.notAssessed > 0 && (
        <MathRow
          label={`Not Yet Assessed${
            counts.employeesNotAssessed > 0
              ? ` (incl. ${plural(counts.employeesNotAssessed, 'Employee', 'Employees')} with none)`
              : ''
          }`}
          value={
            <>
              <Op>−</Op>
              {counts.notAssessed}
            </>
          }
        />
      )}
      <MathRow ruleAbove label="Assessed" value={score.total} />
      <MathRow label="Capable or Better" value={score.count} />
      {score.percent !== null && (
        <MathRow
          total
          label={resultLabel}
          value={
            <>
              {score.count}
              <Op>÷</Op>
              {score.total}
              <Op>=</Op>
              {score.percent}%
            </>
          }
        />
      )}
    </Box>
  );
};

/** A strongest / weakest capability row, its % opening that capability's own Breakdown. */
const CapabilityLine: React.FC<{
  label: string;
  capability: CapabilityResult;
  pairs: CapabilityPair[];
  teams: Team[];
}> = ({ label, capability, pairs, teams }) => (
  <ScoreLine
    label={label}
    name={capability.name}
    percent={capability.ratio.percent}
    tooltip={
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <Typography sx={{ ...TIP_TEXT, whiteSpace: 'nowrap' }}>
          The percentage of employees who are capable or better.
        </Typography>
        <Breakdown
          pairs={pairs.filter((p) => p.capabilityId === capability.capabilityId)}
          teams={teams}
          resultLabel={capability.name}
        />
      </Box>
    }
  />
);

/**
 * The Team Capability score at the top of the admin panel, over the teams
 * selected in the panel's filter. Left: the % at full height, its Breakdown
 * and Scoring on hover. Right: the strongest and weakest capabilities.
 * Hidden while there is nothing to score.
 */
/**
 * What a capability score opens: the sum behind it, role by role, and how a
 * level is scored. The Team Capability card's tooltip, and what a % on the
 * Insights table opens.
 */
export const CapabilityScoreBreakdown: React.FC<{ pairs: CapabilityPair[]; teams: Team[]; resultLabel: string }> = ({
  pairs,
  teams,
  resultLabel
}) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
    <Breakdown
      pairs={pairs}
      teams={teams}
      resultLabel={resultLabel}
      note="Each role has its own capabilities, so scores are worked out role by role."
    />
    <Typography sx={{ ...TIP_HEADING, mt: '6px' }}>Scoring</Typography>
    <Typography sx={TIP_TEXT}>
      Elliott grades each Roleplay as weak, developing, capable or advanced, based on the criteria set by Team Owners.
      An employee&apos;s level on a capability is the average of their 5 most recent Roleplays on it, rounded down.
      Passing 40% or fewer of their 5 most recent Quiz Questions lowers it by one level.
    </Typography>
  </Box>
);

const TeamCapabilityScore: React.FC<{ teams: Team[] }> = ({ teams }) => {
  const teamIds = teams.map((t) => t.id);
  // One pair per person and capability of their role in these teams, and the line week by week.
  const { data: pairsView } = useCapabilityPairs(teamIds, teamIds.length > 0);
  const { data: trendView } = useCapabilityTrend(teamIds, teamIds.length > 0);
  const pairs = pairsView?.pairs ?? [];
  if (pairs.length === 0) return null;

  const score = capabilityScore(pairs);
  const strongest = strongestCapability(pairs);
  const weakest = weakestCapability(pairs);
  const trend = trendFromApi(trendView);
  const trendEvents = trend.events;

  const scoreTooltip = <CapabilityScoreBreakdown pairs={pairs} teams={teams} resultLabel="Team Capability" />;

  return (
    <>
      <ScoreCard percent={score.percent} tooltip={scoreTooltip}>
        {strongest && (
          <CapabilityLine label="Strongest Capability" capability={strongest} pairs={pairs} teams={teams} />
        )}
        {weakest && <CapabilityLine label="Weakest Capability" capability={weakest} pairs={pairs} teams={teams} />}
      </ScoreCard>
      <SectionDivide />

      {/* The last 6 months, week by week (Ronan's Capability mode design), Capability only. */}
      <ChartSection>
        <CapabilityTrend
          compact
          title="Last 6 Months"
          window={trend.window}
          series={trend.series}
          events={trendEvents}
        />
      </ChartSection>
      <SectionDivide />
    </>
  );
};

export default TeamCapabilityScore;
